/**
 * Onboarding Stage Drill-Down Page
 *
 * Shows all users at a specific funnel stage.
 * Reached via clicking a stage bar in the user funnel chart.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import DataTable from '../../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';
import {
  ArrowLeftIcon,
  UserIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const STAGE_LABELS = {
  signed_up: 'Signed Up',
  onboarding_skipped: 'Closed Wizard',
  viewed_options: 'Opened Wizard',
  join_code_attempted: 'Tried Join Code',
  searched_church: 'Searched Church',
  join_request_sent: 'Sent Join Request',
  lead_submitted: 'Submitted Lead',
  notify_me: 'Notify Me',
  personal_group_started: 'Personal Group',
  org_creation_started: 'Started Org Creation',
  awaiting_approval: 'Awaiting Approval',
  invited: 'Invited',
  activated: 'Activated',
};

const STAGE_COLORS = {
  signed_up: 'bg-gray-100 text-gray-800',
  onboarding_skipped: 'bg-orange-100 text-orange-800',
  viewed_options: 'bg-blue-100 text-blue-800',
  join_code_attempted: 'bg-indigo-100 text-indigo-800',
  searched_church: 'bg-purple-100 text-purple-800',
  join_request_sent: 'bg-cyan-100 text-cyan-800',
  lead_submitted: 'bg-teal-100 text-teal-800',
  notify_me: 'bg-slate-100 text-slate-800',
  personal_group_started: 'bg-violet-100 text-violet-800',
  org_creation_started: 'bg-yellow-100 text-yellow-800',
  awaiting_approval: 'bg-amber-100 text-amber-800',
  invited: 'bg-lime-100 text-lime-800',
  activated: 'bg-green-100 text-green-800',
};

function daysSince(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export default function OnboardingStageDrillDown() {
  const { currentEnv } = useEnvironment();
  const { stage } = useParams();
  const [searchParams] = useSearchParams();
  const cohort = searchParams.get('cohort');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null);

  const stageLabel = STAGE_LABELS[stage] || stage;
  const stageBadgeColor = STAGE_COLORS[stage] || 'bg-gray-100 text-gray-800';

  // Back navigates to cohort tab if we came from there, funnels otherwise
  const backPath = cohort ? '/onboarding/users' : '/onboarding/funnels';
  const backLabel = cohort ? 'Back to Users' : 'Back to Funnels';

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingStageUsers(stage, cohort);
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[OnboardingStageDrillDown] Error:', err);
      const errorData = err.response?.data?.error;
      const errorMsg =
        typeof errorData === 'string'
          ? errorData
          : errorData?.message || err.message || 'Couldn\'t load users. Try again?';
      setError(errorMsg);
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();
  }, [stage, cohort, currentEnv]);

  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <UserIcon className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{value || row.display_name || 'Unknown'}</p>
            <p className="text-xs text-gray-500">{row.email || '-'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'signup_date',
      header: 'Signed Up',
      sortable: true,
      render: (value) => {
        if (!value) return '-';
        const d = new Date(value);
        return `${d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })}`;
      },
    },
    {
      key: 'days_at_stage',
      header: 'Days at Stage',
      sortable: true,
      render: (value) => {
        const days = value ?? 0;
        return (
          <span
            className={`font-medium ${
              days > 7 ? 'text-red-600' : days > 3 ? 'text-amber-600' : 'text-gray-900'
            }`}
          >
            {days}d
          </span>
        );
      },
    },
    {
      key: 'last_activity',
      header: 'Last Activity',
      sortable: true,
      render: (value) => {
        if (!value) return <span className="text-gray-400">None</span>;
        const days = daysSince(value);
        return (
          <span className="text-gray-700">
            {days === 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days}d ago`}
          </span>
        );
      },
    },
  ];

  // Error state
  if (error && dataEnv === currentEnv) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(backPath)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 min-h-[44px]"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          {backLabel}
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Couldn't load users</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const users = data?.users || [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div className="flex items-center justify-between">
        <div>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
            <button
              onClick={() => navigate('/onboarding')}
              className="hover:text-gray-900 min-h-[44px] flex items-center"
            >
              Onboarding
            </button>
            <ChevronRightIcon className="w-3.5 h-3.5" />
            <button
              onClick={() => navigate(backPath)}
              className="hover:text-gray-900 min-h-[44px] flex items-center"
            >
              {cohort ? 'Users' : 'Funnels'}
            </button>
            <ChevronRightIcon className="w-3.5 h-3.5" />
            <span className="text-gray-900 font-medium">{stageLabel}</span>
          </nav>

          {/* Back + Title */}
          <button
            onClick={() => navigate(backPath)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2 min-h-[44px]"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span className="text-sm">{backLabel}</span>
          </button>

          {loading ? (
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-gray-900">
                Users at Stage
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${stageBadgeColor}`}
              >
                {stageLabel}
              </span>
              {cohort && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-700">
                  Cohort: week of {cohort}
                  <button
                    onClick={() => navigate(`/onboarding/funnels/users/${stage}`)}
                    className="hover:text-indigo-900"
                    title="Remove cohort filter"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Summary stat */}
      {!loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <UserIcon className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">{users.length}</span>{' '}
            {users.length === 1 ? 'person' : 'people'} currently at this stage
          </p>
        </div>
      )}

      {/* Users Table */}
      <DataTable
        data={users}
        columns={columns}
        loading={loading}
        onRowClick={(user) =>
          navigate(`/onboarding/users/${user.id || user.userId}`)
        }
        emptyMessage="No one is at this stage right now. That's a good sign!"
      />
    </div>
  );
}
